
import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { useIsMobile } from '@/hooks/use-mobile';
import { MultilineAxisTick } from '@/utils/chart-text-utils';

interface BarChartComponentProps {
  data: any[];
  dataKey: string;
  nameKey: string;
  color?: string;
  angle?: number;
  margin?: { top: number; right: number; left: number; bottom: number };
  formatter?: (value: any, name: string) => [string, string];
  enableMultilineLabels?: boolean;
}

const BarChartComponent = ({
  data,
  dataKey,
  nameKey,
  color = '#4f46e5',
  angle = -45,
  margin,
  formatter,
  enableMultilineLabels = false
}: BarChartComponentProps) => {
  const isMobile = useIsMobile();

  // Responsive margins and settings - adjusted for multiline labels
  const responsiveMargin = margin || {
    top: 20,
    right: isMobile ? 20 : 30,
    left: isMobile ? 15 : 20,
    bottom: enableMultilineLabels ? (isMobile ? 80 : 100) : (isMobile ? 60 : 80)
  };

  const tickFontSize = isMobile ? 10 : 11;
  const xAxisHeight = enableMultilineLabels ? (isMobile ? 70 : 90) : (isMobile ? 50 : 70);
  const yAxisWidth = isMobile ? 40 : 50;
  const maxLabelLength = isMobile ? 10 : 12;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={responsiveMargin}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey={nameKey}
          angle={enableMultilineLabels ? 0 : angle}
          textAnchor={enableMultilineLabels ? "middle" : "end"}
          height={xAxisHeight}
          tick={enableMultilineLabels ? 
            (props) => <MultilineAxisTick {...props} maxLength={maxLabelLength} fontSize={tickFontSize} /> :
            { fontSize: tickFontSize }
          }
          interval={0}
        />
        <YAxis width={yAxisWidth} tick={{ fontSize: tickFontSize }} />
        <Tooltip 
          formatter={formatter} 
          labelStyle={{ color: '#333' }}
          wrapperStyle={{ 
            backgroundColor: 'white', 
            padding: '8px', 
            border: '1px solid #ccc',
            fontSize: isMobile ? '12px' : '14px',
            borderRadius: '4px'
          }}
        />
        <Legend 
          verticalAlign="top" 
          height={36}
          wrapperStyle={{ fontSize: isMobile ? '12px' : '14px' }}
        />
        <Bar 
          dataKey={dataKey} 
          fill={color} 
          name="Views"
          minPointSize={2}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BarChartComponent;
